import { useState } from 'react';
import { Box } from '@material-ui/core';
import { SBase, STabs, SText } from '@setvi/shared/components';
import { InternalNote } from 'Services/Query/Auto-RFQ';
import { useRFQStore } from 'pages/auto-rfq/store';
import CustomerNoteDialog from '../customer-note-dialog';
import InternalNoteDialog from '../internal-note-dialog';

type NotesTab = 'customer' | 'internal';

interface NotesDialogProps {
  rfqItemId?: string;
  customerNote?: string;
  // Title = matched product name (shown in the header).
  productName?: string;
  defaultTab?: NotesTab;
  onClose: () => void;
  onSaveCustomer: (note: string) => void;
  onDeleteCustomer: () => void;
  onSaveInternal: (content: string) => void | Promise<void>;
  onEditInternal: (note: InternalNote, content: string) => void | Promise<void>;
  onDeleteInternal: (note: InternalNote) => void | Promise<void>;
}

const TAB_DESCRIPTION: Record<NotesTab, string> = {
  customer:
    'Shared with your customer — appears as a dedicated column in the exported quote.',
  internal:
    'Private to your team. Use it for vendor notes, pricing flags, or selection rationale.'
};

export const NotesDialog = ({
  rfqItemId,
  customerNote,
  productName,
  defaultTab = 'customer',
  onClose,
  onSaveCustomer,
  onDeleteCustomer,
  onSaveInternal,
  onEditInternal,
  onDeleteInternal
}: NotesDialogProps) => {
  const [tab, setTab] = useState<NotesTab>(defaultTab);

  // Read the customer note live from the store rather than trusting the
  // snapshot captured when the dialog was opened. A collaborator's save lands
  // in the store via `rfq_item.updated` (useRfqLiveUpdates); subscribing here
  // keeps the open modal in sync without a close/reopen (DEV-471). Falls back
  // to the passed prop when the item isn't in the store yet.
  const liveCustomerNote = useRFQStore(s => {
    const product = s.products.find(p => p.id === rfqItemId);
    return (product as { customerNote?: string } | undefined)?.customerNote;
  });
  const effectiveCustomerNote = liveCustomerNote ?? customerNote;

  return (
    <SBase
      disablePadding
      style={{ background: '#FFFFFF' }}
      header={{
        title: productName || 'Notes',
        onClose,
        divider: true
      }}
      width="520px"
      maxWidth="92vw"
      height="auto">
      <Box style={{ padding: '8px 20px 0' }}>
        <STabs
          newDesign
          divider={false}
          currentTab={tab}
          tabsList={[
            { label: 'Customer Note', value: 'customer' },
            { label: 'Internal Note', value: 'internal' }
          ]}
          handleTabChange={value => setTab(value as NotesTab)}
        />
      </Box>

      <Box style={{ padding: '12px 20px 0' }}>
        <SText
          size="sm"
          fontColor="#8A97A5"
          noLineClamp
          title={TAB_DESCRIPTION[tab]}
        />
      </Box>

      {tab === 'customer' ? (
        <CustomerNoteDialog
          hideHeader
          initialNote={effectiveCustomerNote}
          onClose={onClose}
          onSave={onSaveCustomer}
          onDelete={onDeleteCustomer}
        />
      ) : (
        <InternalNoteDialog
          hideHeader
          rfqItemId={rfqItemId}
          onClose={onClose}
          onSave={onSaveInternal}
          onEdit={onEditInternal}
          onDelete={onDeleteInternal}
        />
      )}
    </SBase>
  );
};

export default NotesDialog;
